import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Standardized PDF generation utility for all reports
 * Ensures consistent pagination, table formatting, and margins
 * Combines small sections on the same page to reduce whitespace
 * 
 * @param {Array} sections - Array of section objects with {id, title}
 * @param {string} filename - PDF filename (e.g., 'goal-line-report.pdf')
 * @param {Function} setError - Error handler function
 */
export const generateStandardizedPDF = async (sections, filename, setError) => {
  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 45;
    const availableHeight = pageHeight - (margin * 2);
    const sectionSpacing = 0; // Space between sections on same page
    
    console.log('Generating PDF with sections:', sections.map(s => s.id));
    console.log('PDF page size (inches):', pageWidth, 'x', pageHeight);

    let pageAdded = false;
    let currentPageY = margin;
    
    // Dynamically find all sections to include personnel analysis
    const allSections = [...sections];
    
    // Find personnel sections dynamically for Goal Line reports
    const personnelHeader = document.querySelector('[data-section="goalline-personnel-header"]');
    if (personnelHeader) {
      const insertIndex = allSections.findIndex(s => s.id === 'goalline-concepts-section');
      allSections.splice(insertIndex, 0, { id: 'goalline-personnel-header', title: 'Personnel Analysis Header' });
      
      // Find all personnel subsections
      let personnelIndex = 0;
      while (document.querySelector(`[data-section="goalline-personnel-${personnelIndex}"]`)) {
        allSections.splice(insertIndex + 1 + personnelIndex, 0, 
          { id: `goalline-personnel-${personnelIndex}`, title: `Personnel Group ${personnelIndex + 1}` });
        personnelIndex++;
      }
    }
    
    // Find personnel sections dynamically for Base Downs reports
    const baseDownsPersonnelHeader = document.querySelector('[data-section="base-downs-personnel-section"]');
    if (baseDownsPersonnelHeader) {
      const insertIndex = allSections.findIndex(s => s.id === 'base-downs-other-personnel-section');
      if (insertIndex !== -1) {
        // Find all base downs personnel subsections and insert them before "Other Personnel"
        let personnelIndex = 0;
        while (document.querySelector(`[data-section="base-downs-personnel-${personnelIndex}"]`)) {
          allSections.splice(insertIndex + personnelIndex, 0, 
            { id: `base-downs-personnel-${personnelIndex}`, title: `Base Downs Personnel Group ${personnelIndex + 1}` });
          personnelIndex++;
        }
      }
    }
    
    // Find personnel sections dynamically for VanGo Base Downs reports
    const vangoPersonnelHeader = document.querySelector('[data-section="vango-personnel-header"]');
    if (vangoPersonnelHeader) {
      const insertIndex = allSections.findIndex(s => s.id === 'vango-other-personnel-section');
      if (insertIndex !== -1) {
        // Find all VanGo personnel subsections and insert them before "Other Personnel"
        let personnelIndex = 0;
        while (document.querySelector(`[data-section="vango-personnel-${personnelIndex}"]`)) {
          allSections.splice(insertIndex + personnelIndex, 0, 
            { id: `vango-personnel-${personnelIndex}`, title: `VanGo Personnel Group ${personnelIndex + 1}` });
          personnelIndex++;
        }
      }
    }
    
    // Find distance sections dynamically for VanGo Final Downs reports
    const vangoFinalDistanceSection = document.querySelector('[data-section="vango-final-distance-section"]');
    if (vangoFinalDistanceSection) {
      const insertIndex = allSections.findIndex(s => s.id === 'vango-final-targets-section');
      if (insertIndex !== -1) {
        // Find all VanGo Final Downs distance subsections and insert them before "Pass Target Analysis"
        let distanceIndex = 0;
        while (document.querySelector(`[data-section="vango-final-distance-${distanceIndex}"]`)) {
          allSections.splice(insertIndex + distanceIndex, 0, 
            { id: `vango-final-distance-${distanceIndex}`, title: `Distance Analysis ${distanceIndex + 1}` });
          distanceIndex++;
        }
      }
    }
    
    for (let i = 0; i < allSections.length; i++) {
      const section = allSections[i];
      const element = document.querySelector(`[data-section="${section.id}"]`);
      if (!element) continue;
      
      // Force new page if section requires it
      if (section.forceNewPage && pageAdded) {
        pdf.addPage();
        currentPageY = margin;
      }
      
      try {
        // Apply standardized table constraints
        const { canvas, cleanup } = await captureElementWithTableConstraints(element);
        
        // Calculate final dimensions with scaling
        const maxContentWidth = pageWidth - (margin * 2);
        const scaleFactor = Math.min(1, maxContentWidth / canvas.width);
        const finalWidth = canvas.width * scaleFactor;
        const finalHeight = canvas.height * scaleFactor;
        
        // If section is too tall for any page, split it
        if (finalHeight > availableHeight) {
          // Start new page for large sections
          if (pageAdded) {
            pdf.addPage();
            currentPageY = margin;
          }
          
          // Split large section across multiple pages
          currentPageY = await addLargeSectionToPDF(pdf, canvas, finalWidth, finalHeight, pageWidth, pageHeight, margin);
          pageAdded = true;
          
        } else {
          // Handle normal-sized sections
          const requiredSpace = finalHeight + (pageAdded ? sectionSpacing : 0);
          const availableSpace = pageHeight - margin - currentPageY;
          
          // Start new page if section won't fit
          if (pageAdded && requiredSpace > availableSpace) {
            pdf.addPage();
            currentPageY = margin;
          }
          
          // Add spacing if not first section on page
          if (pageAdded && currentPageY === margin) {
            // New page, no spacing needed
          } else if (pageAdded) {
            // Special handling for Goal Line report - no spacing between summary and personnel sections
            if (filename.includes('goal-line') && 
                (section.id === 'goalline-yardline-section' || section.id === 'goalline-personnel-header')) {
              // No spacing for Goal Line personnel sections
            } else {
              currentPageY += sectionSpacing;
            }
          }
          
          // Add the section image
          const imgData = canvas.toDataURL('image/png', 1.0);
          pdf.addImage(imgData, 'PNG', margin, currentPageY, finalWidth, finalHeight);
          
          // Update current Y position
          currentPageY += finalHeight;
          pageAdded = true;
        }
        
        // Clean up temporary styles
        cleanup();
        
      } catch (sectionError) {
        console.error(`Error capturing section ${section.id}:`, sectionError);
      }
    }
    
    // If no sections were processed, show error
    if (!pageAdded) {
      throw new Error('No report sections found to generate PDF');
    }
    
    pdf.save(filename);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    setError('Failed to generate PDF. Please try again.');
  }
};

/**
 * Handles large sections that need to be split across multiple pages
 * Returns the Y position after placing the content
 */
const addLargeSectionToPDF = async (pdf, canvas, finalWidth, finalHeight, pageWidth, pageHeight, margin) => {
  const availableHeight = pageHeight - (margin * 2);
  let sourceY = 0;
  let remainingHeight = canvas.height;
  let currentY = margin;
  
  while (remainingHeight > 0) {
    // Calculate how much of the canvas fits on this page
    const canvasHeightPerPage = (availableHeight * canvas.width) / finalWidth;
    const pageContentHeight = Math.min(canvasHeightPerPage, remainingHeight);
    
    // Create canvas for this page
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = pageContentHeight;
    const pageCtx = pageCanvas.getContext('2d');
    
    // Fill with white background
    pageCtx.fillStyle = '#ffffff';
    pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    
    // Draw the section of content for this page
    pageCtx.drawImage(
      canvas,
      0, sourceY,                    // Source position
      canvas.width, pageContentHeight, // Source dimensions
      0, 0,                          // Destination position
      pageCanvas.width, pageCanvas.height // Destination dimensions
    );
    
    // Add page to PDF
    const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
    const pdfImageHeight = (pageContentHeight * finalWidth) / canvas.width;
    
    pdf.addImage(pageImgData, 'PNG', margin, currentY, finalWidth, pdfImageHeight);
    
    // Update current Y position
    currentY += pdfImageHeight;
    
    // Move to next section
    sourceY += pageContentHeight;
    remainingHeight -= pageContentHeight;
    
    // Add new page if there's more content
    if (remainingHeight > 0) {
      pdf.addPage();
      currentY = margin; // Reset Y position for new page
    }
  }
  
  // Return the current Y position so next section can continue on same page if there's space
  return currentY;
};

/**
 * Captures an element with standardized table constraints
 * Returns canvas and cleanup function
 */
const captureElementWithTableConstraints = async (element) => {
  // Store original styles
  const originalWidth = element.style.width;
  const originalMaxWidth = element.style.maxWidth;
  
  // Apply element constraints - smaller width for more compact content
  element.style.width = '700px';
  element.style.maxWidth = '700px';
  
  // Force Grid items to stay side-by-side for PDF
  const gridItems = element.querySelectorAll('.MuiGrid-item');
  const originalGridStyles = [];
  gridItems.forEach((item, index) => {
    originalGridStyles[index] = {
      flexBasis: item.style.flexBasis,
      maxWidth: item.style.maxWidth,
      width: item.style.width
    };
    
    // Force 50% width for md={6} items within Q2/Q4 sections
    if (item.closest('[data-section="q2-analysis-section"], [data-section="q4-analysis-section"]') && 
        item.classList.contains('MuiGrid-grid-md-6')) {
      item.style.flexBasis = '50%';
      item.style.maxWidth = '50%';
      item.style.width = '50%';
    }
  });
  
  // Store original font sizes and apply smaller fonts for PDF
  const originalElementFontSize = element.style.fontSize;
  element.style.fontSize = '10px';
  
  // Force white backgrounds for all elements for clean PDF
  const originalBackgrounds = [];
  const elementsWithBackground = element.querySelectorAll('*');
  elementsWithBackground.forEach((el, index) => {
    originalBackgrounds[index] = el.style.backgroundColor;
    // Force white background for all elements except chips/buttons with specific colors
    if (!el.classList.contains('MuiChip-root') && 
        !el.classList.contains('MuiButton-contained') &&
        !el.closest('.MuiChip-root') &&
        !el.closest('.MuiButton-contained')) {
      el.style.backgroundColor = '#ffffff';
    }
  });
  
  // Apply smaller fonts to all text elements
  const allElements = element.querySelectorAll('*');
  const originalFontSizes = [];
  allElements.forEach((el, index) => {
    originalFontSizes[index] = el.style.fontSize;
    const tagName = el.tagName.toLowerCase();
    
    // Skip font reduction for summary stat boxes (preserve readability)
    if (el.closest('[data-section="base-downs-summary-section"]') && 
        (el.style.fontSize === '1.8rem' || el.style.fontSize === '0.9rem')) {
      return; // Don't modify these elements
    }
    
    // Apply appropriate font sizes based on element type
    switch(tagName) {
      case 'h1':
        el.style.fontSize = '20px';
        break;
      case 'h2':
        el.style.fontSize = '18px';
        break;
      case 'h3':
        // For summary boxes, use larger font
        if (el.closest('[data-section="base-downs-summary-section"]')) {
          el.style.fontSize = '22px';
        } else {
          el.style.fontSize = '16px';
        }
        break;
      case 'h4':
      case 'h5':
      case 'h6':
        // Smaller fonts for Goal Line reports to fit more content
        if (element.closest('[data-section*="goalline"]')) {
          el.style.fontSize = '13px';
        } else {
          el.style.fontSize = '14px';
        }
        break;
      case 'th':
        el.style.fontSize = '14px';
        break;
      case 'td':
        el.style.fontSize = '13px';
        break;
      default:
        // For summary section elements with explicit sizes, preserve larger fonts
        if (el.closest('[data-section="base-downs-summary-section"]')) {
          if (el.style.fontSize === '1.8rem') {
            el.style.fontSize = '20px'; // Large readable number
            return;
          }
          if (el.style.fontSize === '0.9rem') {
            el.style.fontSize = '12px'; // Readable label
            return;
          }
        }
        
        // Preserve readable font sizes for Chip components (Run Concepts ovals)
        if (el.classList.contains('MuiChip-label') || el.closest('.MuiChip-root')) {
          el.style.fontSize = '16px'; // Much larger size for chip text readability
          el.style.fontWeight = 'bold';
          el.style.color = '#ffffff'; // Ensure white text
          return;
        }
        
        // Handle chip font sizes specifically
        if (el.style.fontSize === '1.1rem') {
          el.style.fontSize = '16px'; // Convert chip font to large readable size
          return;
        }
        
        // Don't override existing explicit font sizes
        if (el.style.fontSize && el.style.fontSize !== '') {
          // Keep existing font size if it's already set
          return;
        }
        // Smaller default font for Goal Line reports
        if (element.closest('[data-section*="goalline"]')) {
          el.style.fontSize = '12px';
        } else {
          el.style.fontSize = '14px';
        }
        break;
    }
  });
  
  // Apply table constraints
  const tables = element.querySelectorAll('table');
  const originalTableStyles = [];
  const originalCellStyles = [];
  
  tables.forEach((table, index) => {
    originalTableStyles[index] = {
      width: table.style.width,
      maxWidth: table.style.maxWidth,
      tableLayout: table.style.tableLayout
    };
    
    // Special handling for tables in Q2/Q4 sections to fit side-by-side
    if (table.closest('[data-section="q2-analysis-section"], [data-section="q4-analysis-section"]')) {
      table.style.width = '100%';
      table.style.maxWidth = '350px'; // Smaller width for side-by-side layout
      table.style.tableLayout = 'fixed';
    } else {
      table.style.width = '100%';
      table.style.maxWidth = '780px';
      table.style.tableLayout = 'fixed';
    }
    
    // Also constrain individual cells for better wrapping
    const cells = table.querySelectorAll('td, th');
    originalCellStyles[index] = [];
    cells.forEach((cell, cellIndex) => {
      originalCellStyles[index][cellIndex] = {
        whiteSpace: cell.style.whiteSpace,
        wordWrap: cell.style.wordWrap,
        overflow: cell.style.overflow
      };
      cell.style.whiteSpace = 'normal';
      cell.style.wordWrap = 'break-word';
      cell.style.overflow = 'hidden';
    });
  });
  
  // Capture canvas
  const canvas = await html2canvas(element, { 
    scale: 1.0,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: 700,
    height: element.scrollHeight,
    windowWidth: 700
  });
  
  // Cleanup function to restore original styles
  const cleanup = () => {
    element.style.width = originalWidth;
    element.style.maxWidth = originalMaxWidth;
    element.style.fontSize = originalElementFontSize;
    
    // Restore original Grid styles
    gridItems.forEach((item, index) => {
      if (originalGridStyles[index]) {
        item.style.flexBasis = originalGridStyles[index].flexBasis;
        item.style.maxWidth = originalGridStyles[index].maxWidth;
        item.style.width = originalGridStyles[index].width;
      }
    });
    
    // Restore original font sizes
    allElements.forEach((el, index) => {
      el.style.fontSize = originalFontSizes[index];
    });
    
    // Restore original backgrounds
    elementsWithBackground.forEach((el, index) => {
      el.style.backgroundColor = originalBackgrounds[index];
    });
    
    tables.forEach((table, index) => {
      if (originalTableStyles[index]) {
        table.style.width = originalTableStyles[index].width;
        table.style.maxWidth = originalTableStyles[index].maxWidth;
        table.style.tableLayout = originalTableStyles[index].tableLayout;
      }
      
      // Restore cell styles
      if (originalCellStyles[index]) {
        const cells = table.querySelectorAll('td, th');
        cells.forEach((cell, cellIndex) => {
          if (originalCellStyles[index][cellIndex]) {
            cell.style.whiteSpace = originalCellStyles[index][cellIndex].whiteSpace;
            cell.style.wordWrap = originalCellStyles[index][cellIndex].wordWrap;
            cell.style.overflow = originalCellStyles[index][cellIndex].overflow;
          }
        });
      }
    });
  };
  
  return { canvas, cleanup };
};

/**
 * Standard section configurations for different report types
 */
export const REPORT_SECTIONS = {
  GOAL_LINE: [
    { id: 'goalline-summary-section', title: 'Goal Line Report' },
    { id: 'goalline-tendencies-section', title: 'Key Tendencies' },
    { id: 'goalline-verification-section', title: 'Data Verification' }
  ],
  
  FINAL_DOWNS: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'foundation-section', title: 'Foundation Data' },
    { id: 'distance-section', title: 'Distance Analysis' },
    { id: 'pass-targets-section', title: 'Pass Targets' },
    { id: 'success-section', title: 'Success Analysis' },
    { id: 'verification-section', title: 'Data Verification' }
  ],
  
  PASSING_TARGETS: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'summary-section', title: 'Summary' },
    { id: 'receivers-section', title: 'Receiver Analysis' },
    { id: 'insights-section', title: 'Key Insights' }
  ],
  
  DEFENSIVE_VULNERABILITY: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'summary-section', title: 'Summary' },
    { id: 'offensive-personnel-section', title: 'Run - Offensive Personnel' },
    { id: 'formation-section', title: 'Run - Formation' },
    { id: 'run-concept-section', title: 'Run - Run Concept' },
    { id: 'point-of-attack-section', title: 'Run - Point of Attack' },
    { id: 'box-players-section', title: 'Run - Box Players' },
    { id: 'defensive-personnel-section', title: 'Run - Defensive Personnel' },
    { id: 'missed-tackles-section', title: 'Run - Missed Tackles' },
    { id: 'pass-offensive-personnel-section', title: 'Pass - Offensive Personnel' },
    { id: 'pass-formation-section', title: 'Pass - Formation' },
    { id: 'pass-depth-section', title: 'Pass - Pass Depth' },
    { id: 'yards-after-catch-section', title: 'Pass - Yards After Catch' },
    { id: 'protections-section', title: 'Pass - Protections' },
    { id: 'pass-routes-and-depth-section', title: 'Pass - Pass Routes and Depth' },
    { id: 'pass-coverage-section', title: 'Pass - Pass Coverage' },
    { id: 'pass-coverage-defenders-section', title: 'Pass - Coverage Defenders' }
  ],
  
  TWO_MINUTE_REPORT: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'foundation-section', title: 'Foundation Data' },
    { id: 'q2-analysis-section', title: 'Final 2-Minutes of Half: Personnel & Play Type Breakdown' },
    { id: 'q4-analysis-section', title: 'Final 2-Minutes of Game: Personnel & Play Type Breakdown' },
    { id: 'critical-tendencies-section', title: 'Critical Tendencies' }
  ],
  
  RED_ZONE_REPORT: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'title-section', title: 'Report Title' },
    { id: 'summary-section', title: 'Summary' },
    { id: 'personnel-usage-section', title: 'Personnel Usage' },
    { id: 'personnel-analysis-section', title: 'Personnel Analysis' },
    { id: 'other-personnel-section', title: 'Other Personnel' }
  ],
  
  BASE_DOWNS: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'base-downs-summary-section', title: 'Summary' },
    { id: 'base-downs-personnel-section', title: 'Personnel Analysis' },
    { id: 'base-downs-other-personnel-section', title: 'Other Personnel' },
    { id: 'base-downs-takeaways-section', title: 'Critical Takeaways', forceNewPage: true }
  ],
  
  VANGO_BASE_DOWNS: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'vango-verification-section', title: 'Data Verification' },
    { id: 'vango-personnel-summary-section', title: 'Personnel Usage Summary' },
    { id: 'vango-personnel-header', title: 'Detailed Personnel Analysis' },
    // Personnel detail sections will be dynamically generated
    { id: 'vango-other-personnel-section', title: 'Other Personnel Groupings' },
    { id: 'vango-takeaways-section', title: 'Critical Defensive Takeaways', forceNewPage: true }
  ],
  
  VANGO_FINAL_DOWNS: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'vango-final-verification-section', title: 'Data Verification - True Final Downs Filter' },
    { id: 'vango-final-foundation-section', title: 'Foundation Data' },
    { id: 'vango-final-distance-section', title: 'Distance Breakdown & Analysis' },
    // Distance detail sections will be dynamically generated
    { id: 'vango-final-targets-section', title: 'Pass Target Analysis' },
    { id: 'vango-final-insights-section', title: 'Key Insights' },
    { id: 'vango-final-tendencies-section', title: 'Critical Tendencies', forceNewPage: true }
  ],
  
  VANGO_TWO_MINUTES: [
    { id: 'report-header-section', title: 'Report Header' },
    { id: 'vango-two-minutes-foundation-section', title: 'Foundation Data' },
    { id: 'vango-two-minutes-q2-personnel-section', title: 'First Half 2-Minute (Q2) Personnel Breakdown' },
    { id: 'vango-two-minutes-q2-passing-section', title: 'Q2 Passing Analysis' },
    { id: 'vango-two-minutes-q4-personnel-section', title: 'Game 2-Minute (Q4) Personnel Breakdown' },
    { id: 'vango-two-minutes-q4-passing-section', title: 'Q4 Passing Analysis' },
    { id: 'vango-two-minutes-tendencies-section', title: 'Critical Tendencies', forceNewPage: true }
  ]
};